import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RiArrowDownSLine, RiCheckLine, RiGlobalLine } from "react-icons/ri";

import { LANGUAGES_OPTIONS } from "../../utils/optionUtils";

const LANGUAGE_OPTIONS = Array.from(LANGUAGES_OPTIONS.values());

const normalizeLanguageKey = (language) => {
  if (!language) {
    return "en";
  }

  if (language.toLowerCase().startsWith("zh")) {
    return "zh_Hans";
  }

  return "en";
};

const getActiveLanguageLabel = (languageKey) =>
  languageKey === "zh_Hans" ? "中文" : "EN";

const LanguageSwitcher = ({
  align = "right",
  className = "",
  menuPosition = "bottom",
  onSelect,
}) => {
  const { i18n, t } = useTranslation("navigation");
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const currentLanguage = useMemo(
    () => normalizeLanguageKey(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage],
  );

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const activeLanguage =
    LANGUAGE_OPTIONS.find((option) => option.KEY === currentLanguage) ||
    LANGUAGE_OPTIONS[0];

  const handleSelectLanguage = async (languageKey) => {
    await i18n.changeLanguage(languageKey);
    setIsOpen(false);
    onSelect?.();
  };

  const menuAlignment = align === "left" ? "left-0" : "right-0";
  const menuPositionClasses =
    menuPosition === "top"
      ? "bottom-[calc(100%+0.5rem)]"
      : "top-[calc(100%+0.5rem)]";
  const menuOrigin =
    menuPosition === "top"
      ? align === "left"
        ? "origin-bottom-left"
        : "origin-bottom-right"
      : menuAlignment;

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="flex items-center justify-between gap-3 rounded-full px-2 py-2 text-sm font-medium text-slate-700 transition  hover:text-sky-600"
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <span className="flex items-center gap-2">
          <RiGlobalLine className="text-base text-slate-500" />
          <span>{getActiveLanguageLabel(activeLanguage.KEY)}</span>
        </span>
        <RiArrowDownSLine
          className={`text-lg transition ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen ? (
        <div
          className={`absolute z-50 w-44 rounded-2xl border border-slate-200 bg-white py-2 shadow-xl ${menuAlignment} ${menuPositionClasses} ${menuOrigin}`}
          role="menu"
        >
          {LANGUAGE_OPTIONS.map((option) => {
            const isActive = option.KEY === currentLanguage;

            return (
              <button
                className={`flex w-full items-center justify-between px-5 py-2 text-left text-sm transition ${
                  isActive
                    ? "bg-sky-50 text-sky-600"
                    : "text-slate-700 hover:bg-slate-50 hover:text-sky-600"
                }`}
                key={option.KEY}
                onClick={() => handleSelectLanguage(option.KEY)}
                role="menuitem"
                type="button"
              >
                <span>{t(option.LABEL)}</span>
                {isActive ? (
                  <RiCheckLine className="text-base" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default LanguageSwitcher;
